import React, { useState } from "react";
import {
  Grid,
  Typography,
  Box,
  Divider,
  TextField,
  Button,
  Paper,
  Container,
  IconButton,
  Card,
  CardContent,
  Alert,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import ShareIcon from "@mui/icons-material/Share";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import SendIcon from '@mui/icons-material/Send';
import { motion } from 'framer-motion';
import Header from "./Header";
import Footer from "./Footer";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    message: "",
  });
  const [showAlert, setShowAlert] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log(formData);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
    setFormData({
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      message: "",
    });
  };

  const gradientStyle = {
    background: 'linear-gradient(to right, rgb(45, 11, 90), rgb(193, 6, 163))',
  };

  const gradientText = {
    background: 'linear-gradient(to right, rgb(45, 11, 90), rgb(193, 6, 163))',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  const contactMethods = [
    {
      icon: PhoneIcon,
      title: "Call Us",
      details: ["+91 90738 54542", "+91 90738 54542"],
    },
    {
      icon: WhatsAppIcon,
      title: "WhatsApp",
      details: ["+91 90738 54542"],
    },
    {
      icon: EmailIcon,
      title: "Mail Us",
      details: ["sales@zoodonix.com"],
    },
    {
      icon: ShareIcon,
      title: "Social Media",
      details: ["Follow us on social platforms"],
      socialIcons: [FacebookIcon, TwitterIcon, InstagramIcon, LinkedInIcon],
    },
    {
      icon: LocationOnIcon,
      title: "Address",
      details: ["Kolkata, West Bengal, India"],
    },
  ];

  return (
    <>
      <Header />
      <Box sx={{ minHeight: '100vh', backgroundColor: '#ffffff' }}>
        <Container maxWidth="xl" sx={{ py: 8 }}>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Header Section */}
            <motion.div variants={itemVariants}>
              <Box sx={{ textAlign: "center", mb: 8 }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: "bold",
                    ...gradientText,
                    mb: 2,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                  }}
                >
                  Get in Touch
                </Typography>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: 100 }}
                  transition={{ duration: 1, delay: 0.5 }}
                >
                  <Box
                    sx={{
                      width: "100px",
                      height: "4px",
                      margin: "0 auto",
                      ...gradientStyle,
                      borderRadius: "2px",
                      mb: 4,
                    }}
                  />
                </motion.div>
                <Typography
                  variant="h6"
                  sx={{
                    color: "rgba(0, 0, 0, 0.7)",
                    maxWidth: "600px",
                    mx: "auto",
                  }}
                >
                  Connect with us through multiple channels. We're here to help you grow your business.
                </Typography>
              </Box>
            </motion.div>

            {/* Contact Methods Grid */}
            <motion.div variants={itemVariants}>
              <Grid container spacing={3} sx={{ mb: 8 }}>
                {contactMethods.map((method, index) => (
                  <Grid item xs={12} sm={6} lg={2.4} key={index}>
                    <motion.div
                      whileHover={{ y: -10, scale: 1.02 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Card
                        sx={{
                          height: '100%',
                          background: '#ffffff',
                          borderRadius: 4,
                          textAlign: "center",
                          p: 3,
                          border: '2px solid #f0f0f0',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                          '&:hover': {
                            border: '2px solid rgba(193, 6, 163, 0.3)',
                            boxShadow: '0 12px 40px rgba(193, 6, 163, 0.2)',
                          },
                        }}
                      >
                        <CardContent>
                          <motion.div
                            whileHover={{ rotate: 360 }}
                            transition={{ duration: 0.6 }}
                          >
                            <Box
                              sx={{
                                ...gradientStyle,
                                borderRadius: "50%",
                                width: 80,
                                height: 80,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto 20px",
                                boxShadow: '0 4px 20px rgba(193, 6, 163, 0.3)',
                              }}
                            >
                              <method.icon sx={{ fontSize: 35, color: "white" }} />
                            </Box>
                          </motion.div>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: "bold",
                              mb: 2,
                              ...gradientText,
                            }}
                          >
                            {method.title}
                          </Typography>
                          {method.details.map((detail, idx) => (
                            <Typography
                              key={idx}
                              variant="body2"
                              sx={{ color: "text.secondary", mb: 0.5 }}
                            >
                              {detail}
                            </Typography>
                          ))}
                          {method.socialIcons && (
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
                              {method.socialIcons.map((SocialIcon, idx) => (
                                <motion.div
                                  key={idx}
                                  whileHover={{ scale: 1.2 }}
                                  whileTap={{ scale: 0.9 }}
                                >
                                  <IconButton
                                    size="small"
                                    sx={{
                                      color: 'white',
                                      ...gradientStyle,
                                      width: 35,
                                      height: 35,
                                      '&:hover': {
                                        background: 'linear-gradient(to right, rgb(35, 8, 70), rgb(173, 4, 143))',
                                      },
                                    }}
                                  >
                                    <SocialIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </motion.div>
                              ))}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </motion.div>

            {/* Alert */}
            {showAlert && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <Alert
                  severity="success"
                  sx={{ mb: 4, maxWidth: '800px', mx: 'auto' }}
                >
                  Thank you for reaching out! We'll get back to you soon.
                </Alert>
              </motion.div>
            )}

            {/* Main Content Section */}
            <motion.div variants={itemVariants}>
              <Paper
                elevation={10}
                sx={{
                  borderRadius: 6,
                  overflow: 'hidden',
                  background: '#ffffff',
                  border: '1px solid #f0f0f0',
                }}
              >
                <Grid container>
                  {/* Left Side: Image */}
                  <Grid item xs={12} lg={6}>
                    <Box
                      sx={{
                        position: 'relative',
                        height: { xs: '300px', lg: '100%' },
                        minHeight: '400px',
                        overflow: 'hidden',
                      }}
                    >
                      <motion.div
                        initial={{ scale: 1.1, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 1 }}
                      >
                        <Box
                          component="img"
                          src="https://www.kre8iveminds.com/assets/images/kre8iveminds-contact-us.jpg"
                          alt="Contact us illustration"
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      </motion.div>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'linear-gradient(45deg, rgba(45, 11, 90, 0.8), rgba(193, 6, 163, 0.8))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          variant="h3"
                          sx={{
                            color: 'white',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                          }}
                        >
                          Let's Connect
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Right Side: Form */}
                  <Grid item xs={12} lg={6}>
                    <Box sx={{ p: { xs: 4, md: 6 } }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: "bold",
                          mb: 1,
                          ...gradientText,
                          fontSize: { xs: '2rem', md: '2.5rem' },
                        }}
                      >
                        Tell us your need
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: "600",
                          mb: 2,
                          color: 'rgb(45, 11, 90)',
                        }}
                      >
                        Zoodonix is always with you
                      </Typography>
                      <Box
                        sx={{
                          width: "80px",
                          height: "4px",
                          ...gradientStyle,
                          borderRadius: "2px",
                          mb: 3,
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{ mb: 4, color: "text.secondary", fontSize: '1.1rem' }}
                      >
                        Tell us how you would like to grow your business. We're here to make it happen.
                      </Typography>

                      {/* Form */}
                      <Box component="form" onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              name="firstName"
                              label="Your First Name"
                              variant="outlined"
                              value={formData.firstName}
                              onChange={handleChange}
                              required
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  '&.Mui-focused fieldset': {
                                    borderColor: 'rgb(193, 6, 163)',
                                    borderWidth: '2px',
                                  },
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                  color: 'rgb(193, 6, 163)',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              name="lastName"
                              label="Your Last Name"
                              variant="outlined"
                              value={formData.lastName}
                              onChange={handleChange}
                              required
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  '&.Mui-focused fieldset': {
                                    borderColor: 'rgb(193, 6, 163)',
                                    borderWidth: '2px',
                                  },
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                  color: 'rgb(193, 6, 163)',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              name="phone"
                              label="Your Phone Number"
                              variant="outlined"
                              value={formData.phone}
                              onChange={handleChange}
                              required
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  '&.Mui-focused fieldset': {
                                                                        borderColor: 'rgb(193, 6, 163)',
                                    borderWidth: '2px',
                                  },
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                  color: 'rgb(193, 6, 163)',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              name="email"
                              label="Your Email"
                              type="email"
                              variant="outlined"
                              value={formData.email}
                              onChange={handleChange}
                              required
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  '&.Mui-focused fieldset': {
                                    borderColor: 'rgb(193, 6, 163)',
                                    borderWidth: '2px',
                                  },
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                  color: 'rgb(193, 6, 163)',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              name="message"
                              label="Tell us a little more"
                              variant="outlined"
                              multiline
                              rows={4}
                              value={formData.message}
                              onChange={handleChange}
                              required
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  '&.Mui-focused fieldset': {
                                    borderColor: 'rgb(193, 6, 163)',
                                    borderWidth: '2px',
                                  },
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                  color: 'rgb(193, 6, 163)',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <motion.div
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                endIcon={<SendIcon />}
                                sx={{
                                  ...gradientStyle,
                                  color: "white",
                                  py: 2,
                                  px: 4,
                                  borderRadius: 3,
                                  textTransform: "none",
                                  fontSize: '1.1rem',
                                  fontWeight: 'bold',
                                  boxShadow: '0 4px 20px rgba(193, 6, 163, 0.4)',
                                  '&:hover': {
                                    background: 'linear-gradient(to right, rgb(35, 8, 70), rgb(173, 4, 143))',
                                    boxShadow: '0 6px 25px rgba(193, 6, 163, 0.6)',
                                  },
                                }}
                              >
                                Send Message
                              </Button>
                            </motion.div>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </motion.div>

            {/* Additional Features Section */}
            <motion.div variants={itemVariants}>
              <Box sx={{ mt: 8, textAlign: 'center' }}>
                <Typography
                  variant="h4"
                  sx={{
                    ...gradientText,
                    fontWeight: 'bold',
                    mb: 4,
                  }}
                >
                  Why Choose Zoodonix?
                </Typography>
                <Grid container spacing={4}>
                  {[
                    {
                      title: "24/7 Support",
                      description: "Round-the-clock assistance for all your needs",
                      icon: "🕐"
                    },
                    {
                      title: "Expert Team",
                      description: "Experienced professionals ready to help",
                      icon: "👥"
                    },
                    {
                      title: "Quick Response",
                      description: "Fast turnaround time for all inquiries",
                      icon: "⚡"
                    },
                    {
                      title: "Custom Solutions",
                      description: "Tailored approaches for your business",
                      icon: "🎯"
                    }
                  ].map((feature, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <motion.div
                        whileHover={{ y: -5 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Card
                          sx={{
                            background: '#ffffff',
                            border: '2px solid #f0f0f0',
                            borderRadius: 3,
                            p: 3,
                            textAlign: 'center',
                            height: '100%',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                            '&:hover': {
                              border: '2px solid rgba(193, 6, 163, 0.3)',
                              boxShadow: '0 8px 30px rgba(193, 6, 163, 0.15)',
                            },
                          }}
                        >
                          <Typography
                            variant="h2"
                            sx={{ mb: 2 }}
                          >
                            {feature.icon}
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              ...gradientText,
                              fontWeight: 'bold',
                              mb: 1,
                            }}
                          >
                            {feature.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'rgba(0, 0, 0, 0.6)',
                            }}
                          >
                            {feature.description}
                          </Typography>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </motion.div>

            {/* Call to Action Section */}
            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  mt: 8,
                  textAlign: 'center',
                  background: '#ffffff',
                  border: '2px solid #f0f0f0',
                  borderRadius: 4,
                  p: 6,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    ...gradientText,
                    fontWeight: 'bold',
                    mb: 2,
                  }}
                >
                  Ready to Get Started?
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'rgba(0, 0, 0, 0.7)',
                    mb: 4,
                    maxWidth: '600px',
                    mx: 'auto',
                  }}
                >
                  Join thousands of satisfied customers who trust Zoodonix for their business growth.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        ...gradientStyle,
                        color: 'white',
                        py: 2,
                        px: 4,
                        borderRadius: 3,
                        fontWeight: 'bold',
                        textTransform: 'none',
                        boxShadow: '0 4px 20px rgba(193, 6, 163, 0.4)',
                        '&:hover': {
                          background: 'linear-gradient(to right, rgb(35, 8, 70), rgb(173, 4, 143))',
                          boxShadow: '0 6px 25px rgba(193, 6, 163, 0.6)',
                        },
                      }}
                    >
                      Get Free Consultation
                    </Button>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      variant="outlined"
                      size="large"
                      sx={{
                        borderColor: 'rgb(193, 6, 163)',
                        color: 'rgb(45, 11, 90)',
                        py: 2,
                        px: 4,
                        borderRadius: 3,
                        fontWeight: 'bold',
                        borderWidth: '2px',
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: 'rgb(193, 6, 163)',
                          background: 'rgba(193, 6, 163, 0.1)',
                          borderWidth: '2px',
                        },
                      }}
                    >
                      View Our Services
                    </Button>
                  </motion.div>
                </Box>
              </Box>
            </motion.div>

            {/* Statistics Section */}
            <motion.div variants={itemVariants}>
              <Box sx={{ mt: 8 }}>
                <Grid container spacing={4}>
                  {[
                    { number: "500+", label: "Happy Clients" },
                    { number: "1000+", label: "Projects Completed" },
                    { number: "24/7", label: "Support Available" },
                    { number: "5+", label: "Years Experience" }
                  ].map((stat, index) => (
                    <Grid item xs={6} md={3} key={index}>
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Box
                          sx={{
                            textAlign: 'center',
                            p: 3,
                            background: '#ffffff',
                            borderRadius: 3,
                            border: '2px solid #f0f0f0',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                            '&:hover': {
                              border: '2px solid rgba(193, 6, 163, 0.3)',
                              boxShadow: '0 8px 30px rgba(193, 6, 163, 0.15)',
                            },
                          }}
                        >
                          <Typography
                            variant="h3"
                            sx={{
                              ...gradientText,
                              fontWeight: 'bold',
                              mb: 1,
                            }}
                          >
                            {stat.number}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: 'rgba(0, 0, 0, 0.7)',
                              fontWeight: '500',
                            }}
                          >
                            {stat.label}
                          </Typography>
                        </Box>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </motion.div>
          </motion.div>
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default ContactUs;

